// Maps GET /api/market/subscription items -- one per IPO NSE is tracking,
// each carrying NSE's category-wise bid rows as
// { category, shares_offered, shares_bid, times } -- into the rows the
// landing page's Live IPO Subscription Demand section renders.
//
// NSE's category labels are the only stable key (sub-rows like "Others" and
// "Corporates" repeat under several parents), so top-level categories are
// matched by label, never by array position.
import { deriveBoard, parsePriceRange, toIsoDate } from './ipoCatalogAdapters';
import { formatDateRange, formatDayShort, formatWeekdayShort, todayIso } from './ipoCalendar';

const CATEGORY_MATCHERS = {
  qib: /^qualified institutional buyers/i,
  nii: /^non institutional investors$/i,
  bnii: /^non institutional investors\s*\(bid amount of more than ten lakh/i,
  snii: /^non institutional investors\s*\(bid amount of more than two lakh/i,
  retail: /^(retail individual investors|individual investors)/i,
  employee: /^employees?$/i,
  total: /^total$/i,
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const findCategory = (rows, key) => rows.find((row) => CATEGORY_MATCHERS[key].test(String(row?.category || '').trim()));

const phaseFor = (openOn, closeOn, today) => {
  if (openOn && today < openOn) return 'upcoming';
  if (closeOn && today > closeOn) return 'closed';
  if (closeOn && today === closeOn) return 'closing_today';
  return 'open';
};

const formatPriceBand = (text) => {
  const { low, high } = parsePriceRange(text);
  if (!high) return text || '—';
  return low && low !== high ? `₹${low} – ₹${high}` : `₹${high}`;
};

export const formatMultiple = (value) => (value === null || value === undefined ? '—' : `${value.toFixed(2)}x`);

export const formatShares = (value) => (value ? Math.round(value).toLocaleString('en-IN') : '—');

export const demandLabel = (total) => {
  if (total >= 50) return 'Mega Demand';
  if (total >= 10) return 'Strong Demand';
  if (total >= 1) return 'Fully Subscribed';
  return 'Undersubscribed';
};

/**
 * @param {object} item - one entry of GET /api/market/subscription -> data.items
 * @param {string} today - local ISO date ("2026-09-21"), injectable for tests
 */
export const mapSubscriptionItem = (item, today = todayIso()) => {
  if (!item || (!item.symbol && !item.company)) return null;

  const rows = Array.isArray(item.by_category) ? item.by_category : [];
  const openOn = toIsoDate(item.issue_start);
  const closeOn = toIsoDate(item.issue_end);
  const phase = phaseFor(openOn, closeOn, today);
  const board = deriveBoard(item.series);

  // Before bidding opens NSE reports every category as 0.00 -- that's "no
  // bids yet", not "0x demand", so those rows show dashes instead.
  const hasData = phase !== 'upcoming' && !item.error && rows.length > 0;
  const multiple = (key) => (hasData ? toNumber(findCategory(rows, key)?.times) : null);
  const totalRow = findCategory(rows, 'total');

  return {
    id: item.symbol || item.company,
    symbol: item.symbol || '',
    company: item.company || item.symbol,
    board,
    boardLabel: board === 'sme' ? 'SME' : 'Mainboard',
    phase,
    openOn,
    closeOn,
    windowLabel: openOn && closeOn ? formatDateRange(openOn, closeOn) : '—',
    priceBand: formatPriceBand(item.price),
    priceHigh: parsePriceRange(item.price).high,
    refreshedAt: item.refreshed_at || null,
    hasData,
    error: item.error || null,
    qib: multiple('qib'),
    nii: multiple('nii'),
    bnii: multiple('bnii'),
    snii: multiple('snii'),
    retail: multiple('retail'),
    emp: multiple('employee'),
    total: hasData ? toNumber(totalRow?.times) ?? toNumber(item.subscription_val) : null,
    sharesBid: hasData ? toNumber(totalRow?.shares_bid) : null,
    sharesOffered: hasData ? toNumber(totalRow?.shares_offered) : null,
  };
};

// Live issues first (closing today, then open, by demand), then upcoming by
// opening date, then closed issues, most recent first
const PHASE_RANK = { closing_today: 0, open: 1, upcoming: 2, closed: 3 };

export const compareSubscriptionRows = (a, b) => {
  if (a.phase !== b.phase) return PHASE_RANK[a.phase] - PHASE_RANK[b.phase];
  if (a.phase === 'upcoming') return (a.openOn || '').localeCompare(b.openOn || '');
  if (a.phase === 'closed') return (b.closeOn || '').localeCompare(a.closeOn || '');
  return (b.total ?? -1) - (a.total ?? -1);
};

export const mapSubscriptionItems = (items, today = todayIso()) =>
  (items || [])
    .map((item) => mapSubscriptionItem(item, today))
    .filter(Boolean)
    .sort(compareSubscriptionRows);

/**
 * Up to three callout cards: highest overall demand, closing today, and
 * strongest retail demand -- each a different IPO. Remaining slots are
 * filled with the next most-subscribed live issues.
 */
export const buildSubscriptionHighlights = (rows) => {
  const withBids = rows.filter((row) => row.hasData && row.total !== null);
  const byTotal = [...withBids].sort((a, b) => b.total - a.total);
  const cards = [];
  const used = new Set();

  const pick = (candidates, build) => {
    const row = candidates.find((candidate) => !used.has(candidate.id));
    if (!row) return;
    used.add(row.id);
    cards.push(build(row));
  };

  pick(byTotal, (row) => ({ key: 'top', label: 'Highest Overall Demand', accent: 'indigo', row, metric: 'total' }));
  pick(
    byTotal.filter((row) => row.phase === 'closing_today'),
    (row) => ({ key: 'closing', label: 'Closing Today at 5:00 PM', accent: 'amber', row, metric: 'total' })
  );
  pick(
    withBids.filter((row) => row.retail !== null).sort((a, b) => b.retail - a.retail),
    (row) => ({
      key: 'retail',
      label: row.retail >= 1 ? 'Retail Over-Subscribed' : 'Top Retail Demand',
      accent: 'emerald',
      row,
      metric: 'retail',
    })
  );
  while (cards.length < 3) {
    const before = cards.length;
    pick(
      byTotal.filter((row) => row.phase !== 'closed'),
      (row) => ({ key: `live-${row.id}`, label: 'Live Bidding', accent: 'indigo', row, metric: 'total' })
    );
    if (cards.length === before) break;
  }
  return cards;
};

// "Varmora Granito Limited opens Tue, 22 Sep · 5 more open Wed, 23 Sep"
export const describeNextOpenings = (rows) => {
  const upcoming = rows.filter((row) => row.phase === 'upcoming' && row.openOn);
  if (upcoming.length === 0) return null;

  const byDate = new Map();
  upcoming.forEach((row) => byDate.set(row.openOn, [...(byDate.get(row.openOn) || []), row]));
  const [first, second] = [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b));

  const describe = ([date, group], isFirst) => {
    if (group.length === 1 && isFirst) return `${group[0].company} opens ${formatWeekdayShort(date)}`;
    const count = isFirst ? `${group.length} IPOs` : `${group.length} more`;
    return `${count} open${group.length === 1 ? 's' : ''} ${formatWeekdayShort(date)}`;
  };

  return [describe(first, true), second && describe(second, false)].filter(Boolean).join(' · ');
};

export const statusLabel = (row) => {
  if (row.phase === 'upcoming') return row.openOn ? `Opens ${formatDayShort(row.openOn)}` : 'Upcoming';
  if (row.phase === 'closing_today') return 'Closes Today';
  if (row.phase === 'closed') return 'Closed';
  return 'Live Bidding';
};
